/**
 * @lodex-pro/web-portal/routes/ApiKeys — the operator admin's API keys (operator portal rework
 * PR 9, migration 0213; lx-coord/operator-portal-design.md §5).
 *
 * Keys used to be issued by the hauler; since 0213 the operator's portal admin (and Paelode)
 * issue, rotate and revoke them. A hauler key reads ONE hauler's data, for a hauler Paelode has
 * confirmed for this company. A company-wide key (13.9, D-08, DEC-372) is tied to no hauler: the
 * company's own SCADA or integrator uses it to send tank readings (tanks:read, readings:write).
 *
 * Who sees it: OPERATOR_ADMIN only (MANAGE_OPERATOR_API_KEYS on the API). Anyone else gets a
 * plain sentence and no API call is made. OP_API is not a gate on the page: without it the list
 * still loads and keys can still be revoked; issuing and rotating are hidden (and refused by the
 * API with FEATURE_NOT_ENABLED).
 *
 * The secret is in component state only, from the issue or rotate response, and is dropped on
 * Done. It is never fetched again: the API does not keep it.
 *
 * Times render in the viewer's local time (DEC-004). No ids are shown.
 */

import React from 'react';
import { ApiError, apiErrorReasonValue } from '@lodex-pro/web-shared';
import type {
  HaulerKeyScope,
  IssuedOperatorApiKey,
  OperatorApiKeyHauler,
  OperatorApiKeyView,
  OperatorApiKeysResponse,
  OperatorKeyScope,
} from '@lodex-pro/shared/validation/operator-api-credentials';
import { usePortalMe } from '../hooks/useOperatorPortal';
import {
  useIssuePortalApiKey,
  usePortalApiKeyHaulers,
  usePortalApiKeys,
  useRevokePortalApiKey,
  useRotatePortalApiKey,
} from '../hooks/useOperatorApiKeys';
import { PortalDataError } from '../components/PortalDataError';
import { portalIsAdmin } from '../nav/portal-features';
import { groupDigits, localDateTime } from './ApiUsage';
import { formatNumber } from '@lodex-pro/shared/number-format';

// ── Words ────────────────────────────────────────────────────────────────────

/** The scopes a hauler key can hold (HAULER_KEY_SCOPES), in plain English. Never readings:write. */
export const SCOPE_LABELS: ReadonlyArray<{
  readonly scope: HaulerKeyScope;
  readonly label: string;
}> = [
  { scope: 'tickets:read', label: 'Read tickets' },
  { scope: 'volumes:read', label: 'Read volumes' },
  { scope: 'haul-requests:read', label: 'Read haul requests' },
  { scope: 'haul-requests:write', label: 'Send haul requests' },
  { scope: 'lookups:read', label: 'Read sites, wells and job types' },
  { scope: 'tanks:read', label: 'Read tanks' },
];

/** 13.9: the scopes a company-wide key can hold (OPERATOR_KEY_SCOPES). */
export const COMPANY_SCOPE_LABELS: ReadonlyArray<{
  readonly scope: OperatorKeyScope;
  readonly label: string;
}> = [
  { scope: 'tanks:read', label: 'Read your tank list' },
  { scope: 'readings:write', label: 'Send tank readings' },
];

export const COMPANY_WIDE = 'Company-wide';
export const COMPANY_WIDE_NOTE =
  "For your SCADA or integrator to send tank levels or volumes. It can't read hauler tickets or volumes.";

function scopeLabel(scope: string, kind: OperatorApiKeyView['keyKind'] = 'HAULER_PAIR'): string {
  const list: ReadonlyArray<{ readonly scope: string; readonly label: string }> =
    kind === 'OPERATOR' ? COMPANY_SCOPE_LABELS : SCOPE_LABELS;
  return list.find((s) => s.scope === scope)?.label ?? scope;
}

export const PLAN_OFF_MESSAGE = "API access isn't on your company's plan. Contact Paelode.";
export const NOT_ADMIN_MESSAGE = "Only your company's portal admin can manage API keys.";
export const HAULER_REMOVED_BADGE = 'Paelode has removed this hauler; this key is refused';
export const NO_MAPPING_NOTE =
  'No customer mapping yet: the key will read nothing until the hauler maps your company.';
export const SECRET_ONCE = "Copy it now. It won't be shown again.";
export const REVOKE_REASON_MIN = 8;
export const ROTATE_OVERLAP_DEFAULT = 24;
export const ROTATE_OVERLAP_MAX = 720;

function keysWord(n: number): string {
  return n === 1 ? '1 key' : `${String(n)} keys`;
}

export function limitMessage(limit: number | null): string {
  const allows =
    limit === null
      ? 'Your plan allows a set number of keys'
      : `Your plan allows ${keysWord(limit)}`;
  return `${allows} and all are in use. Revoke one first, or ask Paelode to raise the limit.`;
}

/** An API error as a sentence (codes from the error catalog; operator-side wording). */
export function apiKeyErrorMessage(err: unknown, limit: number | null): string {
  const code = err instanceof ApiError ? err.code : null;
  switch (code) {
    case 'OPERATOR_API_KEY_LIMIT_REACHED': {
      const fromError = Number(apiErrorReasonValue(err, 'key_limit'));
      return limitMessage(Number.isInteger(fromError) && fromError > 0 ? fromError : limit);
    }
    case 'FEATURE_NOT_ENABLED':
      return PLAN_OFF_MESSAGE;
    case 'OPERATOR_API_CREDENTIAL_OPERATOR_NOT_SERVED':
      return "Paelode hasn't confirmed that hauler for your company, so a key can't be issued for it. Contact Paelode.";
    case 'OPERATOR_API_CREDENTIAL_ALREADY_ROTATED':
      return 'This key has already been replaced. Rotate its replacement instead.';
    case 'OPERATOR_API_CREDENTIAL_NOT_ACTIVE':
      return 'This key is no longer active. Issue a new one instead.';
    case 'OPERATOR_API_SCOPE_NOT_FOR_KIND':
      return 'A company-wide key can only read your tank list and send tank readings.';
    case 'PERMISSION_DENIED':
      return "You don't have access to this action.";
    case 'VALIDATION_FAILED':
      return 'Some fields are invalid.';
    default:
      return 'Could not save. Check your connection and try again.';
  }
}

function isExpired(k: OperatorApiKeyView, now: Date): boolean {
  return k.expiresAt !== null && new Date(k.expiresAt).getTime() <= now.getTime();
}

/** Active, Revoked, Expired, Replaced, or "Replaced, expires <local time>" during an overlap. */
export function keyStatus(k: OperatorApiKeyView, now: Date = new Date()): string {
  if (k.status === 'REVOKED') return 'Revoked';
  const expired = isExpired(k, now);
  if (k.rotatedToId !== null) {
    return k.expiresAt !== null && !expired
      ? `Replaced, expires ${localDateTime(k.expiresAt)}`
      : 'Replaced';
  }
  return expired ? 'Expired' : 'Active';
}

export function issuedByLabel(actor: OperatorApiKeyView['issuedByActor']): string {
  switch (actor) {
    case 'OPERATOR':
      return 'Your company';
    case 'PLATFORM':
      return 'Paelode';
    case 'HAULER':
      return 'The hauler';
  }
}

/** The key still authenticates (and so can be revoked). */
function isLive(k: OperatorApiKeyView, now: Date): boolean {
  return k.status === 'ACTIVE' && !isExpired(k, now);
}

// ── Styles (ApiUsage's palette) ──────────────────────────────────────────────

const CARD: React.CSSProperties = {
  background: 'var(--color-bg-secondary)',
  borderRadius: 'var(--radius-card)',
  padding: '12px 16px',
  marginBottom: '16px',
};
const TH: React.CSSProperties = { textAlign: 'left', padding: '8px 12px' };
const TH_R: React.CSSProperties = { textAlign: 'right', padding: '8px 12px' };
const TD: React.CSSProperties = { padding: '8px 12px', verticalAlign: 'top' };
const TD_R: React.CSSProperties = { padding: '8px 12px', textAlign: 'right', verticalAlign: 'top' };
const MUTED: React.CSSProperties = { color: 'var(--color-text-tertiary)' };
const BUTTON: React.CSSProperties = {
  padding: '6px 14px',
  minHeight: '32px',
  cursor: 'pointer',
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: 'var(--radius-pill)',
  fontSize: '13px',
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON,
  background: 'var(--color-accent-blue)',
  border: '1px solid var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const DANGER: React.CSSProperties = {
  ...BUTTON,
  color: 'var(--color-danger)',
  border: '1px solid rgba(243, 139, 139, 0.4)',
};
const INPUT: React.CSSProperties = {
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  padding: '6px 10px',
  fontSize: '13px',
};
const FIELD_LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  marginBottom: '4px',
};
const ERROR: React.CSSProperties = {
  color: 'var(--color-danger)',
  fontSize: '13px',
  margin: '8px 0 0',
};

// ── The secret, once ─────────────────────────────────────────────────────────

function SecretBox({
  issued,
  heading,
  onDone,
}: {
  readonly issued: IssuedOperatorApiKey;
  readonly heading: string;
  readonly onDone: () => void;
}): React.JSX.Element {
  const [copied, setCopied] = React.useState<'idle' | 'copied' | 'failed'>('idle');
  const copy = (): void => {
    void (async (): Promise<void> => {
      try {
        await navigator.clipboard.writeText(issued.secret);
        setCopied('copied');
      } catch {
        setCopied('failed');
      }
    })();
  };
  return (
    <section
      aria-label="New key secret"
      style={{ ...CARD, borderColor: 'var(--color-accent-blue)' }}
    >
      <div style={{ marginBottom: '6px' }}>{heading}</div>
      <p style={{ fontSize: '14px', margin: '0 0 8px' }}>
        <strong>{SECRET_ONCE}</strong>
      </p>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="keep-case"
          aria-label="Secret"
          readOnly
          value={issued.secret}
          onFocus={(e): void => e.target.select()}
          style={{ ...INPUT, fontFamily: 'monospace', flex: '1 1 320px', minWidth: 0 }}
        />
        <button type="button" onClick={copy} style={BUTTON}>
          Copy
        </button>
        <button type="button" onClick={onDone} style={PRIMARY}>
          Done
        </button>
      </div>
      {copied === 'copied' && (
        <p role="status" style={{ ...MUTED, fontSize: '13px', margin: '8px 0 0' }}>
          Copied.
        </p>
      )}
      {copied === 'failed' && (
        <p role="status" style={ERROR}>
          Could not copy. Select the secret and copy it yourself.
        </p>
      )}
      {issued.replaced !== undefined && (
        <p style={{ fontSize: '13px', margin: '8px 0 0' }}>
          The old key stops working {localDateTime(issued.replaced.expiresAt)}.
        </p>
      )}
    </section>
  );
}

// ── Issue ────────────────────────────────────────────────────────────────────

function haulerOptionText(h: OperatorApiKeyHauler): string {
  return h.hasCustomerMapping ? h.haulerName : `${h.haulerName} (no customer mapping yet)`;
}

function IssueForm({
  limit,
  onIssued,
  onCancel,
}: {
  readonly limit: number | null;
  readonly onIssued: (issued: IssuedOperatorApiKey) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const haulers = usePortalApiKeyHaulers();
  const issue = useIssuePortalApiKey();
  const [kind, setKind] = React.useState<'HAULER_PAIR' | 'OPERATOR'>('HAULER_PAIR');
  const [haulerTenantId, setHaulerTenantId] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [scopes, setScopes] = React.useState<readonly HaulerKeyScope[]>(['tickets:read']);
  // 13.9: a company-wide key offers only its two scopes, both checked to start with.
  const [companyScopes, setCompanyScopes] = React.useState<readonly OperatorKeyScope[]>([
    'tanks:read',
    'readings:write',
  ]);
  const [error, setError] = React.useState<string | null>(null);

  const companyWide = kind === 'OPERATOR';
  const list = haulers.data?.haulers;
  const chosen = list?.find((h) => h.haulerTenantId === haulerTenantId);
  const labelOk = label.trim().length >= 2 && label.trim().length <= 120;
  const canSubmit =
    labelOk &&
    !issue.isPending &&
    (companyWide ? companyScopes.length > 0 : chosen !== undefined && scopes.length > 0);
  const showFields = companyWide || (list !== undefined && list.length > 0);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    issue.mutate(
      companyWide
        ? { keyKind: 'OPERATOR', label: label.trim(), scopes: [...companyScopes] }
        : // No keyKind: a hauler key is the default, which the API running mid-deploy accepts.
          { haulerTenantId, label: label.trim(), scopes: [...scopes] },
      {
        onSuccess: (issued) => onIssued(issued),
        onError: (err) => setError(apiKeyErrorMessage(err, limit)),
      },
    );
  };

  return (
    <form aria-label="Issue a key" onSubmit={submit} style={CARD}>
      <div style={{ marginBottom: '12px' }}>Issue a key</div>
      <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
        <legend style={FIELD_LABEL}>Kind of key</legend>
        {(
          [
            ['HAULER_PAIR', 'For a hauler'],
            ['OPERATOR', 'Company-wide (tank readings)'],
          ] as const
        ).map(([value, text]) => (
          <label
            key={value}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}
          >
            <input
              type="radio"
              name="key-kind"
              value={value}
              checked={kind === value}
              onChange={(): void => {
                setError(null);
                setKind(value);
              }}
            />
            {text}
          </label>
        ))}
        {companyWide && (
          <p style={{ ...MUTED, fontSize: '13px', margin: '6px 0 0' }}>{COMPANY_WIDE_NOTE}</p>
        )}
      </fieldset>
      {!companyWide && haulers.isLoading && <p role="status">Loading haulers…</p>}
      {!companyWide && haulers.isError && <p role="alert">Could not load your haulers.</p>}
      {!companyWide && list !== undefined && list.length === 0 && (
        <p style={{ ...MUTED, fontSize: '13px' }}>
          Paelode hasn&rsquo;t confirmed any haulers for your company yet, so there is nothing to
          issue a hauler key for. Contact Paelode.
        </p>
      )}
      {showFields && (
        <>
          {!companyWide && list !== undefined && (
            <label style={{ display: 'block', marginBottom: '12px' }}>
              <span style={FIELD_LABEL}>Hauler</span>
              <select
                value={haulerTenantId}
                onChange={(e): void => setHaulerTenantId(e.target.value)}
                style={INPUT}
              >
                <option value="">Choose a hauler</option>
                {list.map((h) => (
                  <option key={h.haulerTenantId} value={h.haulerTenantId}>
                    {haulerOptionText(h)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!companyWide && chosen !== undefined && !chosen.hasCustomerMapping && (
            <p
              role="note"
              style={{ color: 'var(--color-warning)', fontSize: '13px', margin: '-4px 0 12px' }}
            >
              {NO_MAPPING_NOTE}
            </p>
          )}
          <label style={{ display: 'block', marginBottom: '12px' }}>
            <span style={FIELD_LABEL}>Label</span>
            <input
              value={label}
              maxLength={120}
              onChange={(e): void => setLabel(e.target.value)}
              placeholder="e.g. SCADA feed"
              style={{ ...INPUT, width: '100%', maxWidth: '360px' }}
            />
            <span style={{ ...MUTED, display: 'block', fontSize: '12px', marginTop: '4px' }}>
              A name that tells you which system uses this key.
            </span>
          </label>
          {companyWide ? (
            <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
              <legend style={FIELD_LABEL}>What the key can do</legend>
              {COMPANY_SCOPE_LABELS.map((s) => (
                <label
                  key={s.scope}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}
                >
                  <input
                    type="checkbox"
                    checked={companyScopes.includes(s.scope)}
                    onChange={(e): void =>
                      setCompanyScopes((prev) =>
                        e.target.checked ? [...prev, s.scope] : prev.filter((x) => x !== s.scope),
                      )
                    }
                  />
                  {s.label}
                </label>
              ))}
              {companyScopes.length === 0 && <p style={ERROR}>Choose at least one.</p>}
            </fieldset>
          ) : (
            <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
              <legend style={FIELD_LABEL}>What the key can do</legend>
              {SCOPE_LABELS.map((s) => (
                <label
                  key={s.scope}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(s.scope)}
                    onChange={(e): void =>
                      setScopes((prev) =>
                        e.target.checked ? [...prev, s.scope] : prev.filter((x) => x !== s.scope),
                      )
                    }
                  />
                  {s.label}
                </label>
              ))}
              {scopes.length === 0 && <p style={ERROR}>Choose at least one.</p>}
            </fieldset>
          )}
        </>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="submit" disabled={!canSubmit} style={PRIMARY}>
          {issue.isPending ? 'Issuing…' : 'Issue key'}
        </button>
        <button type="button" onClick={onCancel} style={BUTTON}>
          Cancel
        </button>
      </div>
      {error !== null && (
        <p role="alert" style={ERROR}>
          {error}
        </p>
      )}
    </form>
  );
}

// ── Rotate / revoke ──────────────────────────────────────────────────────────

/** Who a key is for: its hauler, or (13.9) "Company-wide". */
export function keyKindText(k: OperatorApiKeyView): string {
  return k.keyKind === 'OPERATOR' || k.haulerName === null ? COMPANY_WIDE : k.haulerName;
}

function keyName(k: OperatorApiKeyView): string {
  return `${k.label} (${keyKindText(k)})`;
}

function RotatePanel({
  apiKey,
  limit,
  onRotated,
  onCancel,
}: {
  readonly apiKey: OperatorApiKeyView;
  readonly limit: number | null;
  readonly onRotated: (issued: IssuedOperatorApiKey) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const rotate = useRotatePortalApiKey(apiKey.id);
  const [hours, setHours] = React.useState(String(ROTATE_OVERLAP_DEFAULT));
  const [error, setError] = React.useState<string | null>(null);
  const n = Number(hours);
  const hoursOk = hours.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= ROTATE_OVERLAP_MAX;

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!hoursOk || rotate.isPending) return;
    setError(null);
    rotate.mutate(
      { overlapHours: n },
      {
        onSuccess: (issued) => onRotated(issued),
        onError: (err) => setError(apiKeyErrorMessage(err, limit)),
      },
    );
  };

  return (
    <form aria-label="Rotate key" onSubmit={submit} style={CARD}>
      <div style={{ marginBottom: '6px' }}>Rotate {keyName(apiKey)}?</div>
      <p style={{ ...MUTED, fontSize: '13px', margin: '0 0 12px' }}>
        You get a new secret. The old one keeps working for the overlap so you can switch your
        system over, then stops.
      </p>
      <label style={{ display: 'block', marginBottom: '12px' }}>
        <span style={FIELD_LABEL}>Hours the old key keeps working</span>
        <input
          type="number"
          min={0}
          max={ROTATE_OVERLAP_MAX}
          step={1}
          value={hours}
          onChange={(e): void => setHours(e.target.value)}
          style={{ ...INPUT, width: '100px' }}
        />
        <span style={{ ...MUTED, display: 'block', fontSize: '12px', marginTop: '4px' }}>
          0 to {ROTATE_OVERLAP_MAX} hours. 0 stops the old key straight away.
        </span>
      </label>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="submit" disabled={!hoursOk || rotate.isPending} style={PRIMARY}>
          {rotate.isPending ? 'Rotating…' : 'Rotate key'}
        </button>
        <button type="button" onClick={onCancel} style={BUTTON}>
          Cancel
        </button>
      </div>
      {error !== null && (
        <p role="alert" style={ERROR}>
          {error}
        </p>
      )}
    </form>
  );
}

function RevokePanel({
  apiKey,
  limit,
  onRevoked,
  onCancel,
}: {
  readonly apiKey: OperatorApiKeyView;
  readonly limit: number | null;
  readonly onRevoked: () => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const revoke = useRevokePortalApiKey(apiKey.id);
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const reasonOk = reason.trim().length >= REVOKE_REASON_MIN;

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!reasonOk || revoke.isPending) return;
    setError(null);
    revoke.mutate(
      { reason: reason.trim() },
      {
        onSuccess: () => onRevoked(),
        onError: (err) => setError(apiKeyErrorMessage(err, limit)),
      },
    );
  };

  return (
    <form aria-label="Revoke key" onSubmit={submit} style={CARD}>
      <div style={{ marginBottom: '6px' }}>Revoke {keyName(apiKey)}?</div>
      <p style={{ ...MUTED, fontSize: '13px', margin: '0 0 12px' }}>
        Every call with this key is refused from now on. This can&rsquo;t be undone.
      </p>
      <label style={{ display: 'block', marginBottom: '12px' }}>
        <span style={FIELD_LABEL}>Reason</span>
        <textarea
          value={reason}
          maxLength={500}
          rows={2}
          onChange={(e): void => setReason(e.target.value)}
          style={{ ...INPUT, width: '100%', maxWidth: '480px' }}
        />
        <span style={{ ...MUTED, display: 'block', fontSize: '12px', marginTop: '4px' }}>
          Say why, in at least {REVOKE_REASON_MIN} characters.
        </span>
      </label>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="submit" disabled={!reasonOk || revoke.isPending} style={DANGER}>
          {revoke.isPending ? 'Revoking…' : 'Revoke key'}
        </button>
        <button type="button" onClick={onCancel} style={BUTTON}>
          Cancel
        </button>
      </div>
      {error !== null && (
        <p role="alert" style={ERROR}>
          {error}
        </p>
      )}
    </form>
  );
}

// ── The page ─────────────────────────────────────────────────────────────────

type Panel =
  | { readonly kind: 'none' }
  | { readonly kind: 'issue' }
  | { readonly kind: 'rotate'; readonly key: OperatorApiKeyView }
  | { readonly kind: 'revoke'; readonly key: OperatorApiKeyView }
  | { readonly kind: 'secret'; readonly issued: IssuedOperatorApiKey; readonly heading: string };

function inUseText(data: OperatorApiKeysResponse): string {
  if (data.limit === null) return `${keysWord(data.liveCount)} in use (no limit)`;
  return `${formatNumber(data.liveCount)} of ${keysWord(data.limit)} in use`;
}

function KeysTable({
  data,
  busy,
  onRotate,
  onRevoke,
}: {
  readonly data: OperatorApiKeysResponse;
  readonly busy: boolean;
  readonly onRotate: (k: OperatorApiKeyView) => void;
  readonly onRevoke: (k: OperatorApiKeyView) => void;
}): React.JSX.Element {
  const now = new Date();
  return (
    <table
      aria-label="API keys"
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border-primary)',
        fontSize: '14px',
      }}
    >
      <thead>
        <tr style={{ background: 'var(--color-bg-tertiary)' }}>
          <th style={TH}>Kind</th>
          <th style={TH}>Key</th>
          <th style={TH}>Status</th>
          <th style={TH}>Last used</th>
          <th style={TH_R}>Calls this month</th>
          <th style={TH}>Issued by</th>
          <th style={TH}>
            <span style={{ position: 'absolute', left: '-9999px' }}>Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {data.keys.length === 0 ? (
          <tr>
            <td colSpan={7} style={{ padding: '16px', textAlign: 'center', ...MUTED }}>
              No API keys yet.
            </td>
          </tr>
        ) : (
          data.keys.map((k) => {
            const live = isLive(k, now);
            // A company-wide key has no hauler to be confirmed (haulerConfirmed null).
            const canRotate =
              live && k.rotatedToId === null && data.apiEnabled && k.haulerConfirmed !== false;
            return (
              <tr key={k.id} style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                <td style={TD}>
                  {k.keyKind === 'OPERATOR' || k.haulerName === null ? (
                    <div>{COMPANY_WIDE}</div>
                  ) : (
                    <div>
                      <span style={MUTED}>Hauler: </span>
                      <span>{k.haulerName}</span>
                    </div>
                  )}
                  {k.haulerConfirmed === false && (
                    <div
                      style={{
                        display: 'inline-block',
                        marginTop: '4px',
                        padding: '2px 6px',
                        borderRadius: '12px',
                        background: 'rgba(243, 139, 139, 0.14)',
                        color: 'var(--color-danger)',
                        fontSize: '12px',
                      }}
                    >
                      {HAULER_REMOVED_BADGE}
                    </div>
                  )}
                </td>
                <td style={TD}>
                  <div>{k.label}</div>
                  <div style={{ ...MUTED, fontSize: '12px' }}>
                    {k.scopes.map((sc) => scopeLabel(sc, k.keyKind)).join(', ')}
                  </div>
                </td>
                <td style={TD}>{keyStatus(k, now)}</td>
                <td style={{ ...TD, ...MUTED, fontSize: '13px' }}>
                  {k.lastUsedAt === null ? 'Never' : localDateTime(k.lastUsedAt)}
                </td>
                <td style={{ ...TD_R, fontVariantNumeric: 'tabular-nums' }}>
                  {groupDigits(k.monthRequests)}
                </td>
                <td style={TD}>{issuedByLabel(k.issuedByActor)}</td>
                <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                  {canRotate && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={(): void => onRotate(k)}
                      aria-label={`Rotate ${keyName(k)}`}
                      style={{ ...BUTTON, marginRight: '6px' }}
                    >
                      Rotate
                    </button>
                  )}
                  {live && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={(): void => onRevoke(k)}
                      aria-label={`Revoke ${keyName(k)}`}
                      style={DANGER}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

function ApiKeysManager(): React.JSX.Element {
  const { data, isLoading, isError, error } = usePortalApiKeys();
  const [panel, setPanel] = React.useState<Panel>({ kind: 'none' });
  const [notice, setNotice] = React.useState<string | null>(null);

  const open = (next: Panel): void => {
    setNotice(null);
    setPanel(next);
  };
  const close = (): void => setPanel({ kind: 'none' });

  const atLimit = data !== undefined && data.limit !== null && data.liveCount >= data.limit;

  return (
    <>
      {isLoading && <p role="status">Loading API keys…</p>}
      {isError && <PortalDataError error={error} fallback="Could not load API keys." />}
      {data !== undefined && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap',
              marginBottom: '16px',
            }}
          >
            <div data-testid="keys-in-use" style={{ fontSize: '14px' }}>
              {inUseText(data)}
            </div>
            {data.apiEnabled && panel.kind !== 'issue' && panel.kind !== 'secret' && (
              <button
                type="button"
                disabled={atLimit}
                onClick={(): void => open({ kind: 'issue' })}
                style={PRIMARY}
              >
                Issue a key
              </button>
            )}
          </div>

          {!data.apiEnabled && (
            <p
              role="status"
              style={{
                padding: '12px 16px',
                background: 'rgba(123, 150, 232, 0.18)',
                color: 'var(--color-accent-blue-dark)',
                borderRadius: '12px',
                fontSize: '14px',
              }}
            >
              {PLAN_OFF_MESSAGE}
            </p>
          )}
          {data.apiEnabled && atLimit && panel.kind !== 'secret' && (
            <p role="status" style={{ ...MUTED, fontSize: '13px', marginTop: 0 }}>
              {limitMessage(data.limit)}
            </p>
          )}
          {notice !== null && (
            <p role="status" style={{ fontSize: '13px', marginTop: 0 }}>
              {notice}
            </p>
          )}

          {panel.kind === 'issue' && (
            <IssueForm
              limit={data.limit}
              onCancel={close}
              onIssued={(issued): void =>
                setPanel({
                  kind: 'secret',
                  issued,
                  heading: `Key issued: ${keyName(issued.key)}`,
                })
              }
            />
          )}
          {panel.kind === 'rotate' && (
            <RotatePanel
              key={panel.key.id}
              apiKey={panel.key}
              limit={data.limit}
              onCancel={close}
              onRotated={(issued): void =>
                setPanel({
                  kind: 'secret',
                  issued,
                  heading: `New secret for ${keyName(issued.key)}`,
                })
              }
            />
          )}
          {panel.kind === 'revoke' && (
            <RevokePanel
              key={panel.key.id}
              apiKey={panel.key}
              limit={data.limit}
              onCancel={close}
              onRevoked={(): void => {
                setNotice(`${keyName(panel.key)} is revoked.`);
                close();
              }}
            />
          )}
          {panel.kind === 'secret' && (
            <SecretBox issued={panel.issued} heading={panel.heading} onDone={close} />
          )}

          <KeysTable
            data={data}
            busy={panel.kind === 'secret'}
            onRotate={(k): void => open({ kind: 'rotate', key: k })}
            onRevoke={(k): void => open({ kind: 'revoke', key: k })}
          />
        </>
      )}
    </>
  );
}

export function ApiKeys(): React.JSX.Element {
  const me = usePortalMe();
  const admin = portalIsAdmin(me.data);
  return (
    <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '4px' }}>API keys</h1>
      <p style={{ ...MUTED, fontSize: '13px', marginTop: 0, marginBottom: '16px' }}>
        Keys your systems use with the lodeX API. A hauler key reads one hauler&rsquo;s data, for
        haulers Paelode has confirmed for your company. A company-wide key sends your tank readings.
      </p>
      {me.isError ? (
        <p role="alert">Could not load your account.</p>
      ) : me.data === undefined ? (
        <p role="status">Loading…</p>
      ) : admin ? (
        <ApiKeysManager />
      ) : (
        <p role="status">{NOT_ADMIN_MESSAGE}</p>
      )}
    </main>
  );
}
